import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FinalCTA from '@/components/FinalCTA';
import ScrollToTop from '@/components/ScrollToTop';
import SEO from '@/components/SEO';
import { ShoppingCart, Users, MessageSquare, Settings, Shield, Store, FileText, Search, Navigation, Upload } from 'lucide-react';

const useCases = [
  {
    title: "E-Commerce",
    icon: ShoppingCart,
    description: "Test the full purchase flow from customer checkout to admin order management.",
    browserA: {
      role: "Customer",
      steps: "Sign in \u2192 Browse products \u2192 Add to cart \u2192 Complete checkout",
    },
    browserB: {
      role: "Admin",
      steps: "Open admin portal \u2192 Navigate to orders \u2192 Verify new order appears \u2192 Update order status to \u2018Processing\u2019",
    },
  },
  {
    title: "SaaS Collaboration",
    icon: Users,
    description: "Verify team invite flows work end-to-end across multiple users.",
    browserA: {
      role: "Team Lead",
      steps: "Sign in \u2192 Go to team settings \u2192 Send invite to new member",
    },
    browserB: {
      role: "New Member",
      steps: "Open invite email \u2192 Click accept link \u2192 Complete profile setup \u2192 Verify access to team workspace",
    },
  },
  {
    title: "Real-Time Messaging",
    icon: MessageSquare,
    description: "Ensure messages appear in real time across multiple connected users.",
    browserA: {
      role: "User Alice",
      steps: "Sign in \u2192 Open chat room \u2192 Send message \u2018Hello team!\u2019",
    },
    browserB: {
      role: "User Bob",
      steps: "Sign in \u2192 Open same chat room \u2192 Verify Alice\u2019s message appears \u2192 Reply \u2018Hi Alice!\u2019",
    },
  },
  {
    title: "Admin + End User",
    icon: Settings,
    description: "Confirm that admin configuration changes are immediately reflected for end users.",
    browserA: {
      role: "Admin",
      steps: "Sign in to admin panel \u2192 Navigate to feature flags \u2192 Enable \u2018Dark Mode\u2019 feature",
    },
    browserB: {
      role: "End User",
      steps: "Sign in to app \u2192 Open settings \u2192 Verify \u2018Dark Mode\u2019 toggle is now available",
    },
  },
  {
    title: "Two-Factor Auth",
    icon: Shield,
    description: "Test multi-step authentication flows that span devices or sessions.",
    browserA: {
      role: "Primary",
      steps: "Sign in \u2192 Trigger 2FA \u2192 Wait for code delivery",
    },
    browserB: {
      role: "Secondary",
      steps: "Open email/auth app \u2192 Retrieve verification code \u2192 Enter code in Browser A \u2192 Verify successful login",
    },
  },
  {
    title: "Marketplace",
    icon: Store,
    description: "Validate buyer-seller interactions from listing creation to purchase confirmation.",
    browserA: {
      role: "Seller",
      steps: "Sign in \u2192 Create new listing \u2192 Set price and details \u2192 Publish",
    },
    browserB: {
      role: "Buyer",
      steps: "Browse marketplace \u2192 Find seller\u2019s listing \u2192 Complete purchase \u2192 Verify confirmation",
    },
  },
];

const singleUserExamples = [
  { icon: FileText, label: "Form submission testing" },
  { icon: Search, label: "Search functionality" },
  { icon: Navigation, label: "Navigation flows" },
  { icon: Upload, label: "File upload testing" },
];

const UseCases = () => {
  return (
    <div className="flex flex-col min-h-screen bg-dark-bg relative">
      <SEO
        title="Use Cases - SkyTest.ai | Test Real Business Workflows"
        description="See how SkyTest.ai tests real multi-user business workflows: e-commerce, SaaS collaboration, messaging, admin panels, and more."
      />
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,34,255,0.05)_0%,rgba(10,10,20,0)_70%)] pointer-events-none"></div>

      <Navbar />

      <main className="flex-grow relative">
        {/* Hero Section */}
        <section className="min-h-[50vh] relative flex items-center justify-center section-padding overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(to bottom, hsl(240, 80%, 7%), #0a0a14)',
              }}
            />
            <div
              className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[60%] h-[40%] rounded-full blur-[100px]"
              style={{
                background: 'radial-gradient(circle at center, hsl(210, 80%, 50%) 0%, rgba(0,34,255,0.05) 70%)',
                opacity: 0.3,
                animation: 'pulse-subtle 8s ease-in-out infinite',
              }}
            />
          </div>

          <div className="container max-w-5xl mx-auto flex flex-col items-center text-center z-10">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight tracking-tight max-w-4xl animate-fade-up">
              <span className="text-gradient">Test Your Real Business Workflows</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mb-10 animate-fade-up">
              See how SkyTest coordinates multiple browsers and roles to test the workflows that actually matter to your business.
            </p>
          </div>
        </section>

        <div className="h-16 bg-gradient-to-b from-[#0a0a14] to-dark-bg"></div>

        {/* Use Case Cards */}
        <section className="section-padding relative">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-20 -left-20 w-[40%] h-[40%] rounded-full bg-brand/5 blur-[150px] animate-pulse-subtle" />
          </div>

          <div className="container mx-auto">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center animate-fade-up">
                <span className="text-gradient">Multi-User Workflow Testing</span>
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {useCases.map((useCase, index) => (
                  <div
                    key={index}
                    className="glass-morphism rounded-2xl p-6 md:p-8 animate-fade-up group hover:shadow-brand-sm transition-all duration-300"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand/20 to-brand/40 flex items-center justify-center shrink-0">
                        <useCase.icon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white">{useCase.title}</h3>
                    </div>

                    <p className="text-gray-300 mb-6">{useCase.description}</p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Browser A */}
                      <div className="rounded-xl p-4 bg-blue-500/10 border border-blue-500/20">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-2.5 h-2.5 rounded-full bg-blue-400"></div>
                          <span className="text-sm font-semibold text-blue-300">
                            Browser A: {useCase.browserA.role}
                          </span>
                        </div>
                        <p className="text-sm text-gray-300 leading-relaxed">{useCase.browserA.steps}</p>
                      </div>

                      {/* Browser B */}
                      <div className="rounded-xl p-4 bg-emerald-500/10 border border-emerald-500/20">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-2.5 h-2.5 rounded-full bg-emerald-400"></div>
                          <span className="text-sm font-semibold text-emerald-300">
                            Browser B: {useCase.browserB.role}
                          </span>
                        </div>
                        <p className="text-sm text-gray-300 leading-relaxed">{useCase.browserB.steps}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Single-User Testing Section */}
        <section className="section-padding relative">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -bottom-20 -right-20 w-[40%] h-[40%] rounded-full bg-brand/5 blur-[150px] animate-pulse-subtle" />
          </div>

          <div className="container mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 animate-fade-up">
                <span className="text-gradient">Powerful Single-User Testing Too</span>
              </h2>
              <p className="text-lg text-gray-300 mb-10 animate-fade-up max-w-2xl mx-auto">
                SkyTest isn't limited to multi-role scenarios. It handles all your standard end-to-end testing needs just as well.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {singleUserExamples.map((example, index) => (
                  <div
                    key={index}
                    className="glass-morphism rounded-2xl p-6 animate-fade-up group hover:shadow-brand-sm transition-all duration-300"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand/20 to-brand/40 flex items-center justify-center mx-auto mb-3">
                      <example.icon className="h-5 w-5 text-white" />
                    </div>
                    <p className="text-sm font-medium text-gray-200">{example.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <FinalCTA />
      </main>

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default UseCases;
